import React, { useState } from 'react';
import {
  Container,
  Header,
  Button,
  SpaceBetween,
  Box,
  Alert,
  FormField,
  Input
} from '@cloudscape-design/components';
import bupersLogo from '../assets/BUPERS Logo.png';

function SignInPassword({ onSignIn }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    setError('');
    
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setIsAuthenticating(true);

    try {
      const apiEndpoint = process.env.REACT_APP_AUTH_API_ENDPOINT;
      
      if (!apiEndpoint) {
        throw new Error('Authentication API endpoint not configured');
      }

      const response = await fetch(`${apiEndpoint}/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'login',
          username: username.trim(),
          password: password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      if (data.success && data.credentials) {
        // Store credentials, tokens, and user info
        localStorage.setItem('awsCredentials', JSON.stringify(data.credentials));
        localStorage.setItem('cognitoTokens', JSON.stringify(data.tokens));
        localStorage.setItem('userInfo', JSON.stringify(data.user));
        
        // Pass credentials to parent
        onSignIn(data.credentials, data.user);
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
      setIsAuthenticating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '480px'
      }}>
        <Box textAlign="center" margin={{ bottom: 'xl' }}>
          <img 
            src={bupersLogo} 
            alt="BUPERS Logo" 
            style={{ 
              width: '200px', 
              height: '200px', 
              margin: '0 auto 20px',
              display: 'block',
              objectFit: 'contain',
              filter: 'drop-shadow(0 10px 40px rgba(0,0,0,0.3))'
            }} 
          />
          <div style={{ color: 'white', fontSize: '32px', fontWeight: '600', marginBottom: '8px' }}>
            Bureau of Naval Personnel
          </div>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
            Secure document processing platform
          </div>
        </Box>

        <Container>
          <SpaceBetween direction="vertical" size="l">
            <Box textAlign="center" padding={{ vertical: 'm' }}>
              <div style={{ fontSize: '24px', fontWeight: '600' }}>
                Sign In
              </div>
            </Box>

            {error && (
              <Alert type="error" dismissible onDismiss={() => setError('')}>
                {error}
              </Alert>
            )}

            {isAuthenticating && (
              <Alert type="info">
                <SpaceBetween direction="vertical" size="xs">
                  <Box>Authenticating...</Box>
                  <Box fontSize="body-s" color="text-body-secondary">
                    Please wait while we verify your credentials
                  </Box>
                </SpaceBetween>
              </Alert>
            )}

            <FormField label="Email / Username">
              <Input
                value={username}
                onChange={({ detail }) => setUsername(detail.value)}
                onKeyPress={handleKeyPress}
                disabled={isAuthenticating}
                placeholder="Enter your email or username"
                autoComplete="username"
              />
            </FormField>

            <FormField label="Password">
              <Input
                value={password}
                onChange={({ detail }) => setPassword(detail.value)}
                onKeyPress={handleKeyPress}
                disabled={isAuthenticating}
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </FormField>

            <Button
              variant="primary"
              onClick={handleLogin}
              disabled={isAuthenticating}
              loading={isAuthenticating}
              fullWidth
              iconName="unlocked"
            >
              {isAuthenticating ? 'Signing in...' : 'Sign In'}
            </Button>
          </SpaceBetween>
        </Container>

        <Box textAlign="center" margin={{ top: 'l' }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
            Contact your system administrator for account access
          </div>
        </Box>
      </div>
    </div>
  );
}

export default SignInPassword;
